import { AuthUser, BrokeredProvider } from "../types/auth";
import {
    AuthenticatedState,
    KeycloakOAuthShared,
    Token,
} from "./KeycloakOAuthShared";

/**
 * The in-memory authentication state, either unauthenticated or authenticated with the tokens and
 * provider information.
 */
type State = { status: "unauthenticated" } | AuthenticatedState;

/**
 * The initial unauthenticated state, used before the user has logged in or after they log out.
 */
const UNAUTHENTICATED_STATE: State = { status: "unauthenticated" };

/**
 * Owns the in-memory authentication session shared by the web and native Keycloak OAuth services.
 *
 * This is the single source of truth for the current session: the platform services hold one and
 * write to it through {@link setAuthenticated} / {@link clear}, while token consumers (managers
 * calling `getAccessToken` outside React) read from it synchronously. It is intentionally a plain
 * object rather than React state so those non-render reads keep working; React subscribes to it via
 * `useSyncExternalStore` through {@link subscribe} / {@link getSnapshot}.
 */
export class AuthSession {
    /** The current authentication state, including tokens and provider info when authenticated. */
    private state: State = UNAUTHENTICATED_STATE;

    /** React (and any other) subscribers notified on every state change. */
    private readonly listeners = new Set<() => void>();

    /**
     * Stores the authenticated session and notifies subscribers.
     * @param session The authenticated state built from a fresh login or token refresh.
     */
    public setAuthenticated(session: AuthenticatedState): void {
        this.state = session;
        this._emit();
    }

    /**
     * Clears the session back to unauthenticated and notifies subscribers.
     */
    public clear(): void {
        this.state = UNAUTHENTICATED_STATE;
        this._emit();
    }

    /**
     * Subscribes to session changes. Designed for React's `useSyncExternalStore`.
     * @param listener Called after every state change.
     * @returns An unsubscribe function.
     */
    public readonly subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };

    /**
     * Returns a referentially-stable snapshot of the session for `useSyncExternalStore`: the
     * authentication status as a primitive boolean (never a fresh object, to avoid render loops).
     */
    public readonly getSnapshot = (): boolean => {
        return this.state.status === "authenticated";
    };

    /**
     * Indicates whether the user is currently authenticated.
     */
    public isAuthenticated(): boolean {
        return this.state.status === "authenticated";
    }

    /**
     * Returns the current user, or throws if unauthenticated.
     */
    public getUser(): AuthUser {
        if (this.state.status !== "authenticated") {
            throw new Error("User is not authenticated");
        }
        return this.state.authUser;
    }

    /**
     * Returns the brokered identity provider of the current user, or throws if unauthenticated.
     */
    public getBrokeredProvider(): BrokeredProvider {
        if (this.state.status !== "authenticated") {
            throw new Error("User is not authenticated");
        }
        return this.state.brokeredProvider;
    }

    /**
     * Returns the raw Keycloak access token value, or throws if unauthenticated. Does not refresh:
     * callers are responsible for checking {@link areTokensExpired} first.
     */
    public getKeycloakAccessTokenValue(): string {
        if (this.state.status !== "authenticated") {
            throw new Error("User is not authenticated");
        }
        return this.state.keycloakAccessToken.value;
    }

    /**
     * Returns the raw brokered provider access token value, or throws if unauthenticated. Does not
     * refresh: callers are responsible for checking {@link areTokensExpired} first.
     */
    public getProviderAccessTokenValue(): string {
        if (this.state.status !== "authenticated") {
            throw new Error("User is not authenticated");
        }
        return this.state.providerAccessToken.value;
    }

    /**
     * Indicates whether the current tokens are expired or about to expire. Returns `true` when
     * unauthenticated.
     */
    public areTokensExpired(): boolean {
        if (this.state.status !== "authenticated") {
            return true;
        }
        return KeycloakOAuthShared.areTokensExpired(
            this.state.keycloakAccessToken,
            this.state.providerAccessToken,
        );
    }

    /**
     * Returns the stored brokered provider access token (value and expiry), or `undefined` when
     * unauthenticated. Used to detect whether Keycloak actually reissued the token on the next broker
     * exchange and, if not, keep its previously computed expiry.
     */
    public peekProviderAccessToken(): Token | undefined {
        return this.state.status === "authenticated"
            ? this.state.providerAccessToken
            : undefined;
    }

    /**
     * Returns the stored Keycloak refresh token, or `undefined` when unauthenticated. Used to keep the
     * previous refresh token when Keycloak does not rotate it on refresh.
     */
    public peekKeycloakRefreshToken(): string | undefined {
        return this.state.status === "authenticated"
            ? this.state.keycloakRefreshToken
            : undefined;
    }

    /**
     * Returns the stored Keycloak id token, or `undefined` when unauthenticated. Used to keep the
     * previous id token when Keycloak does not return one on refresh, and for the logout id-token hint.
     */
    public peekKeycloakIdToken(): string | undefined {
        return this.state.status === "authenticated"
            ? this.state.keycloakIdToken
            : undefined;
    }

    /**
     * Notifies every subscriber of a state change.
     * @private
     */
    private _emit(): void {
        this.listeners.forEach((listener) => listener());
    }
}
